import { useEffect, useMemo, useState } from "react";
import { RotateCcw, Upload } from "lucide-react";
import { Button, SelectField } from "@/components/reusable";
import { fetchProvidersCached } from "@/services/providersApi";
import { fetchPlatformUsers } from "@/services/meApi";

export interface PipelineBulkProgress {
  label: string;
}

export interface PipelineBoardAdminFilters {
  providerId: string | null;
  onProviderIdChange: (providerId: string | null) => void;
  /** When set, show the admin-only "All users" filter. */
  uploadedBy?: string | null;
  onUploadedByChange?: (uploadedBy: string | null) => void;
  showUserFilter?: boolean;
}

interface PipelineBoardToolbarProps {
  onAddFiles: () => void;
  /** True while files are uploading — locks Add to avoid half-finished queues. */
  isUploading?: boolean;
  retryFailureCount?: number;
  onRetryFailures?: () => void;
  bulkProgress?: PipelineBulkProgress | null;
  /** Board scope filters (provider; optional admin user filter). */
  adminFilters?: PipelineBoardAdminFilters | null;
}

function PipelineBoardScopeFilters({
  providerId,
  uploadedBy = null,
  onProviderIdChange,
  onUploadedByChange,
  showUserFilter = false,
}: PipelineBoardAdminFilters) {
  const [providerOptions, setProviderOptions] = useState<{ value: string; label: string }[]>([]);
  const [userOptions, setUserOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    // Board scope filter: only active providers are selectable for ingestion scoping.
    fetchProvidersCached()
      .then((res) => {
        if (cancelled) return;
        setProviderOptions(
          res.items
            .filter((p) => p.provider_status === "active")
            .map((p) => ({
              value: p.provider_id,
              label: p.provider_name || p.provider_short_code || p.provider_id,
            }))
        );
      })
      .catch(() => {
        if (!cancelled) setProviderOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!showUserFilter) {
      setUserOptions([]);
      return;
    }
    let cancelled = false;
    fetchPlatformUsers()
      .then((users) => {
        if (cancelled) return;
        setUserOptions(
          users
            .filter((u) => u.user_id)
            .map((u) => ({
              // Filter by kodex_users.user_id (oid); backend also matches legacy email rows.
              value: u.user_id,
              label: u.email || u.user_id,
            }))
        );
      })
      .catch(() => {
        if (!cancelled) setUserOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showUserFilter]);

  const providerScopeLabel = useMemo(() => {
    if (!providerId) return "All providers";
    return providerOptions.find((p) => p.value === providerId)?.label ?? providerId;
  }, [providerId, providerOptions]);

  const userScopeLabel = useMemo(() => {
    if (!uploadedBy) return "All users";
    return userOptions.find((u) => u.value === uploadedBy)?.label ?? uploadedBy;
  }, [uploadedBy, userOptions]);

  return (
    <div
      className="flex min-w-0 flex-nowrap items-center gap-2"
      data-testid="pipeline-admin-filters"
    >
      <SelectField
        value={providerId ?? ""}
        onValueChange={(val) => onProviderIdChange(val.trim() ? val : null)}
        options={providerOptions}
        placeholder="All providers"
        className="!w-auto !max-w-[11rem] shrink-0 [&_button]:!h-7 [&_button]:!rounded-md [&_button]:!px-2 [&_button]:!py-0 [&_button]:!text-[11px]"
      />
      {showUserFilter && onUploadedByChange ? (
        <SelectField
          value={uploadedBy ?? ""}
          onValueChange={(val) => onUploadedByChange(val.trim() ? val : null)}
          options={userOptions}
          placeholder="All users"
          className="!w-auto !max-w-[11rem] shrink-0 [&_button]:!h-7 [&_button]:!rounded-md [&_button]:!px-2 [&_button]:!py-0 [&_button]:!text-[11px]"
        />
      ) : null}
      <span
        className="shrink-0 whitespace-nowrap text-[11px] text-[var(--color-text-secondary)]"
        data-testid="pipeline-admin-scope-label"
        title={
          showUserFilter
            ? `Board scope: ${providerScopeLabel} · ${userScopeLabel}`
            : `Board scope: ${providerScopeLabel}`
        }
      >
        Showing:{" "}
        <span className="font-medium text-[var(--color-text-primary)]">{providerScopeLabel}</span>
        {showUserFilter ? (
          <>
            {" · "}
            <span className="font-medium text-[var(--color-text-primary)]">{userScopeLabel}</span>
          </>
        ) : null}
      </span>
    </div>
  );
}

export function PipelineBoardToolbar({
  onAddFiles,
  isUploading = false,
  retryFailureCount = 0,
  onRetryFailures,
  bulkProgress = null,
  adminFilters = null,
}: PipelineBoardToolbarProps) {
  const bulkDisabled = isUploading || bulkProgress != null;

  return (
    <div className="sticky top-0 z-30 flex flex-wrap items-center justify-start gap-2 border-b border-[var(--color-border-light)] bg-[var(--color-bg-primary)] px-3 py-1.5 md:px-4">
      <div className="flex min-w-0 flex-nowrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          className="h-7 shrink-0 gap-1 px-2.5 text-[11px]"
          onClick={onAddFiles}
          disabled={isUploading}
          data-testid="pipeline-add-files-btn"
          title={isUploading ? "Wait for the current upload to finish" : undefined}
        >
          <Upload className="h-3 w-3" />
          Add files
        </Button>

        {adminFilters ? <PipelineBoardScopeFilters {...adminFilters} /> : null}
      </div>

      {retryFailureCount > 0 && onRetryFailures ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-7 gap-1 px-2.5 text-[11px]"
          onClick={onRetryFailures}
          disabled={bulkDisabled}
          data-testid="pipeline-retry-failures-btn"
        >
          <RotateCcw className="h-3 w-3" />
          Retry failures ({retryFailureCount})
        </Button>
      ) : null}

      {bulkProgress ? (
        <span
          className="text-[11px] font-medium text-[var(--color-text-secondary)]"
          data-testid="pipeline-bulk-progress"
        >
          {bulkProgress.label}
        </span>
      ) : null}
    </div>
  );
}
